import { MoreVertical, Unlink } from 'lucide-react';
import { useMemo } from 'react';

import type { TableColumn } from '@/_shared/components/Table';
import { Avatar, AvatarFallback, AvatarImage } from '@/_shared/components/ui/avatar';
import { Button } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { formatDate, formatDateTimeTitle } from '@/_shared/lib/format';

import type { PendingAction, SlackChannelBinding, SlackUserLink } from './types';
import { channelLabel, slackChannelUrl, slackUserProfileUrl } from './utils';

interface UseSlackTableColumnsOptions {
  isAdmin: boolean;
  slackWorkspaceBaseUrl: string | null;
  onPendingAction: (action: PendingAction) => void;
}

export function useSlackTableColumns({
  isAdmin,
  slackWorkspaceBaseUrl,
  onPendingAction,
}: UseSlackTableColumnsOptions): {
  linkColumns: TableColumn<SlackUserLink>[];
  bindingColumns: TableColumn<SlackChannelBinding>[];
} {
  const linkColumns = useMemo<TableColumn<SlackUserLink>[]>(
    () => [
      {
        id: 'slackUser',
        header: 'Slack user',
        sortable: false,
        cell: (link) => {
          const name = link.slackUserName ?? link.slackUserId;
          return (
            <div className="flex min-w-0 items-center gap-3">
              <Avatar className="size-8 shrink-0">
                <AvatarImage src={link.slackUserAvatarUrl ?? undefined} alt="" />
                <AvatarFallback>{name[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{name}</p>
                <a
                  href={slackUserProfileUrl(slackWorkspaceBaseUrl, link.teamId, link.slackUserId)}
                  target="_blank"
                  rel="noreferrer"
                  title="View profile in Slack"
                  className="block truncate text-xs text-muted-foreground hover:text-primary hover:underline"
                >
                  {link.slackUserHandle ? `@${link.slackUserHandle}` : link.slackUserId}
                </a>
              </div>
            </div>
          );
        },
      },
      {
        id: 'lookoutUser',
        header: 'Lookout user',
        sortable: false,
        width: '14rem',
        cell: (link) =>
          link.userName ? (
            <div className="min-w-0">
              <p className="truncate text-sm text-foreground" title={link.userName}>
                {link.userName}
              </p>
              {link.userEmail && (
                <p className="truncate text-xs text-muted-foreground" title={link.userEmail}>
                  {link.userEmail}
                </p>
              )}
            </div>
          ) : (
            <span className="block truncate text-sm text-muted-foreground" title={link.userEmail ?? link.userId}>
              {link.userEmail ?? link.userId}
            </span>
          ),
      },
      {
        id: 'linked',
        header: 'Connected',
        align: 'right',
        width: '8rem',
        sortable: false,
        cell: (link) => (
          <span className="text-xs text-muted-foreground" title={formatDateTimeTitle(link.createdAt)}>
            {formatDate(link.createdAt)}
          </span>
        ),
      },
      ...(isAdmin
        ? [
            {
              id: 'actions',
              header: '',
              headerAriaLabel: 'Actions',
              align: 'right',
              width: '3.5rem',
              sortable: false,
              cell: (link: SlackUserLink) => {
                const name = link.slackUserName ?? link.userName ?? link.userEmail ?? link.slackUserId;
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="tertiary" iconOnly Icon={<MoreVertical />} aria-label={`Actions for ${name}`} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        variant="destructive"
                        onSelect={() =>
                          onPendingAction({
                            variant: 'unlink',
                            teamId: link.teamId,
                            slackUserId: link.slackUserId,
                            name,
                          })
                        }
                      >
                        <Unlink className="size-4" />
                        Disconnect
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              },
            } satisfies TableColumn<SlackUserLink>,
          ]
        : []),
    ],
    [isAdmin, onPendingAction, slackWorkspaceBaseUrl],
  );

  const bindingColumns = useMemo<TableColumn<SlackChannelBinding>[]>(
    () => [
      {
        id: 'channel',
        header: 'Channel',
        sortable: false,
        cell: (binding) => (
          <div className="min-w-0">
            <a
              href={slackChannelUrl(slackWorkspaceBaseUrl, binding.teamId, binding.channelId)}
              target="_blank"
              rel="noreferrer"
              title="Open channel in Slack"
              className="block truncate text-sm font-medium text-foreground hover:text-primary hover:underline"
            >
              {channelLabel(binding)}
            </a>
            <p className="truncate text-xs text-muted-foreground">
              {binding.boundByHandle || binding.boundByName ? (
                <>
                  Connected by{' '}
                  <a
                    href={slackUserProfileUrl(slackWorkspaceBaseUrl, binding.teamId, binding.boundBy)}
                    target="_blank"
                    rel="noreferrer"
                    title="View profile in Slack"
                    className="hover:text-primary hover:underline"
                  >
                    {binding.boundByHandle ? `@${binding.boundByHandle}` : binding.boundByName}
                  </a>
                </>
              ) : (
                'Connected'
              )}
            </p>
          </div>
        ),
      },
      {
        id: 'bound',
        header: 'Connected',
        align: 'right',
        width: '8rem',
        sortable: false,
        cell: (binding) => (
          <span className="text-xs text-muted-foreground" title={formatDateTimeTitle(binding.updatedAt)}>
            {formatDate(binding.updatedAt)}
          </span>
        ),
      },
      ...(isAdmin
        ? [
            {
              id: 'actions',
              header: '',
              headerAriaLabel: 'Actions',
              align: 'right',
              width: '3.5rem',
              sortable: false,
              cell: (binding: SlackChannelBinding) => {
                const label = channelLabel(binding);
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="tertiary" iconOnly Icon={<MoreVertical />} aria-label={`Actions for ${label}`} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        variant="destructive"
                        onSelect={() =>
                          onPendingAction({
                            variant: 'unbind',
                            teamId: binding.teamId,
                            channelId: binding.channelId,
                            name: label,
                          })
                        }
                      >
                        <Unlink className="size-4" />
                        Disconnect
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              },
            } satisfies TableColumn<SlackChannelBinding>,
          ]
        : []),
    ],
    [isAdmin, onPendingAction, slackWorkspaceBaseUrl],
  );

  return { linkColumns, bindingColumns };
}
